import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowRight, X, AlertCircle, CheckCircle2, File, Link, Camera, Images, ChevronDown, Search } from 'lucide-react'
import { useExtractWords, useUploadFile } from '../hooks/useTextImport'
import { useDecks, isDeckOwner } from '../hooks/useDecks'
import { trackEvent } from '../utils/analytics'
import { fileUploadErrorMessage } from '../utils/fileUploadError'
import { textExtractErrorMessage } from '../utils/textExtractError'
import { useAuthStore, selectUser } from '../stores/authStore'
import { recognize } from '../services/ocr'
import { ImageDecodeError, prepareImageForOcr } from '../services/ocrPrepare'
import { routeOcrResult } from '../services/ocrRouter'
import { useLocale } from '../i18n/context'
import CSVImportModal from '../components/shared/CSVImportModal'
import type { TextImportExtractResponse } from '../types'
import {
  getTextImportFlowState,
  setTextImportDraft,
  setTextImportExtraction,
  clearTextImportFlow,
  type CachedTextImportExtraction,
} from '../utils/textImportFlowState'

const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform)

function isUrl(value: string): boolean {
  const trimmed = value.trim()
  return /^https?:\/\/.+\..+/i.test(trimmed) && !trimmed.includes('\n')
}

// Must match backend TEXT_MAX_CHARS in schemas/text_import.py
const MAX_CHARS = 10_000
// Must match backend _MAX_UPLOAD_BYTES in api/v1/text_import.py
const MAX_FILE_BYTES = 10 * 1024 * 1024

const SERVER_EXTRACTED_EXTS = new Set(['.pdf', '.pptx', '.docx'])

// If clipboard carries both an image and text (e.g. screenshot tools that also
// copy a file path), prefer the image so paste triggers OCR rather than textarea.
function getClipboardImage(dataTransfer: DataTransfer | null): File | null {
  if (!dataTransfer) return null
  for (const item of Array.from(dataTransfer.items)) {
    if (item.kind === 'file' && item.type.startsWith('image/')) return item.getAsFile()
  }
  return null
}

// Errors raised in the browser are kept by kind and turned into text at
// render time, so the banner follows the interface language and its action
// fits the failure: a file that can only fail again offers another file.
type FileErrorKind = 'empty' | 'too_large' | 'unsupported' | 'read_failed'
type OcrErrorState =
  | { kind: 'too_large'; sizeMb: number }
  | { kind: 'no_text' | 'decode' | 'generic' }

// CSV files are handed to CSVImportModal (components/shared/CSVImportModal.tsx)
// rather than parsed here — it is the one implementation of CSV parsing and
// import in the web app, backed by the server's delimiter/header detection.

// ── Main component ────────────────────────────────────────────────────────────

export default function TextImportPage() {
  const { tr } = useLocale()
  const ti = tr.textImport
  const navigate = useNavigate()
  const location = useLocation()
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Target deck from navigation state (coming from DeckDetailPage "Import text" button).
  const navState = location.state as {
    prefillText?: string
    targetDeckId?: string
    targetDeckTitle?: string
  } | null

  // A *return* to this step — via the in-page Back control, the browser back
  // button, or the Android system back gesture, all three land on the exact
  // same history entry — carries the same location.key every time. A fresh
  // visit (from AddContentSheet, DeckDetailPage, "Import another text", …)
  // always pushes a brand-new entry with a brand-new key. Comparing keys is
  // how the draft is restored identically on every return path without
  // intercepting or synthesising any of them, and without ever serialising
  // the draft into history state itself (see textImportFlowState.ts).
  const flowState = getTextImportFlowState()
  const isReturningToStep1 = flowState.entryKey === location.key
  const restoreDraft = isReturningToStep1 ? flowState.draft : null

  const preTargetedDeckId: string | null = restoreDraft ? restoreDraft.pickerDeckId : (navState?.targetDeckId ?? null)
  const preTargetedDeckTitle: string | null = restoreDraft ? restoreDraft.targetDeckTitle : (navState?.targetDeckTitle ?? null)

  // Deck picker: "new" creates a deck; "existing" appends to an existing one.
  // Pre-targeted navigation forces "existing" mode with the deck already selected.
  const [importMode, setImportMode] = useState<'new' | 'existing'>(
    restoreDraft ? restoreDraft.importMode : preTargetedDeckId ? 'existing' : 'new',
  )
  const [pickerDeckId, setPickerDeckId] = useState<string | null>(preTargetedDeckId)
  const [deckSearch, setDeckSearch] = useState('')
  const [pickerOpen, setPickerOpen] = useState(false)
  // Tracks whether the pre-targeted chip is active — cleared when user switches to new-deck mode.
  const [preTargetActive, setPreTargetActive] = useState(
    restoreDraft ? restoreDraft.preTargetActive : !!preTargetedDeckId,
  )

  // Text-mode state — restored from a Back navigation, or pre-filled from Share
  // Target / any other navigate() state, in that priority order.
  const [text, setText] = useState(() => {
    return restoreDraft?.text ?? navState?.prefillText ?? ''
  })
  const [title, setTitle] = useState(() => restoreDraft?.title ?? '')
  const [extractError, setExtractError] = useState<string | null>(null)
  const [loadedFileName, setLoadedFileName] = useState<string | null>(() => restoreDraft?.loadedFileName ?? null)
  // The most recent successful extraction for the text currently in the
  // textarea, reused instead of re-hitting /text-import/extract/ when the
  // person returns here without editing the text (see cache check in
  // handleSubmit). Sourced the same way restoreDraft is — never from
  // history state.
  const [cachedExtraction, setCachedExtraction] = useState<CachedTextImportExtraction | null>(
    isReturningToStep1 ? flowState.extraction : null,
  )

  // NOTE: entry_point=extension_youtube_transcript (Plausible) was retired in Phase 8.14
  // when transcript import was withdrawn — YouTube timedtext API no longer accessible.
  // Do not flag missing data for this entry point in future analytics audits.

  // File handling state
  const [isDragging, setIsDragging] = useState(false)
  const [fileLoading, setFileLoading] = useState(false)
  const [fileError, setFileError] = useState<FileErrorKind | null>(null)

  // A CSV file picked here is handed to CSVImportModal, which owns parsing,
  // mapping and the target-deck choice — this page builds no import request.
  const [csvFile, setCsvFile] = useState<File | null>(null)

  const extract = useExtractWords()
  const uploadFile = useUploadFile()

  // Fetch user's decks for the picker — only when in "existing" mode and no pre-target.
  const user = useAuthStore(selectUser)
  const decksQuery = useDecks(1, 100, null, deckSearch || undefined)
  const ownedDecks = (decksQuery.data?.items ?? []).filter((d) => isDeckOwner(d, user?.id))

  // ── OCR state ────────────────────────────────────────────────────────────────
  type OcrPhase = 'idle' | 'downloading' | 'recognizing' | 'error'
  const [ocrPhase, setOcrPhase] = useState<OcrPhase>('idle')
  const [ocrProgressFraction, setOcrProgressFraction] = useState(0)
  const [ocrError, setOcrError] = useState<OcrErrorState | null>(null)
  // Tracks whether the current textarea content came from OCR so we can show
  // a "check for errors" hint. Cleared when user edits the text or dismisses.
  const [textFromOcr, setTextFromOcr] = useState(() => restoreDraft?.textFromOcr ?? false)

  // Continuously mirrors this step's form state into the flow-level draft
  // store. None of the three ways to leave this step (in-page Back, browser
  // back button, Android system back gesture) give a component a reliable
  // "about to unmount for navigation" hook to save on the way out, so the
  // draft is kept current on every change instead.
  useEffect(() => {
    const selectedDeckTitle = preTargetActive
      ? preTargetedDeckTitle
      : (ownedDecks.find((d) => d.id === pickerDeckId)?.title ?? null)
    setTextImportDraft(location.key, {
      text, title, importMode, pickerDeckId, preTargetActive,
      targetDeckTitle: selectedDeckTitle, loadedFileName, textFromOcr,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, title, importMode, pickerDeckId, preTargetActive, loadedFileName, textFromOcr, ownedDecks])

  // Mirrors the same way — the extraction belonging to the text currently in
  // the textarea, so a return visit (any path) sees the same reuse-or-refetch
  // decision the in-page Back control saw.
  useEffect(() => {
    setTextImportExtraction(location.key, cachedExtraction)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cachedExtraction])

  const ocrImageInputRef = useRef<HTMLInputElement>(null)
  const ocrCameraInputRef = useRef<HTMLInputElement>(null)
  const ocrRunRef = useRef(0)

  // Auto-grow textarea — resize on text change, capped at 320px
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 320)}px`
  }, [text])

  function cancelOcr() {
    ocrRunRef.current++
    setOcrPhase('idle')
    setOcrError(null)
  }

  async function handleOcrImageFile(file: File, entryPoint = 'text_import') {
    if (file.size > MAX_FILE_BYTES) {
      trackEvent('ocr_failed', { reason: 'file_too_large' })
      setOcrPhase('error')
      setOcrError({ kind: 'too_large', sizeMb: file.size / 1024 / 1024 })
      return
    }

    trackEvent('ocr_initiated', { entry_point: entryPoint })
    const runId = ++ocrRunRef.current
    setOcrPhase('downloading')
    setOcrProgressFraction(0)
    setTextFromOcr(false)

    try {
      const prepared = await prepareImageForOcr(file)
      // Tesseract's own status strings are English; the phase heading says
      // the same thing in the interface language, so they are not shown.
      const result = await recognize(prepared, user?.target_language, ({ phase, progress }) => {
        if (ocrRunRef.current !== runId) return
        setOcrPhase(phase === 'download' ? 'downloading' : 'recognizing')
        setOcrProgressFraction(progress)
      })

      if (ocrRunRef.current !== runId) return

      // Apply the router's noise normalisation but skip the routing decision —
      // the destination is already text-import.
      const decision = routeOcrResult(result.text)

      if (decision.kind === 'empty') {
        trackEvent('ocr_failed', { reason: 'empty' })
        setOcrPhase('error')
        setOcrError({ kind: 'no_text' })
        return
      }

      trackEvent('ocr_succeeded')
      trackEvent(decision.kind === 'quick-lookup' ? 'ocr_routed_short' : 'ocr_routed_long')
      setText(decision.text)
      setTextFromOcr(true)
      setExtractError(null)
      setOcrPhase('idle')
    } catch (err: unknown) {
      if (ocrRunRef.current !== runId) return
      trackEvent('ocr_failed', { reason: 'recognition_error' })
      setOcrPhase('error')
      setOcrError({ kind: err instanceof ImageDecodeError ? 'decode' : 'generic' })
    }
  }

  // Clipboard-paste OCR: while the page is mounted, Ctrl+V / Cmd+V with an image
  // in the clipboard starts OCR immediately. Plain-text paste continues to flow
  // into the textarea normally (we only intercept when an image is found).
  // Mobile excluded: clipboard images are OS-mediated; camera FAB is the intended path.
  const handleOcrImageFileRef = useRef(handleOcrImageFile)
  useEffect(() => { handleOcrImageFileRef.current = handleOcrImageFile })

  useEffect(() => {
    function handlePaste(e: ClipboardEvent) {
      if (window.matchMedia('(max-width: 767px)').matches) return
      const file = getClipboardImage(e.clipboardData)
      if (!file) return
      e.preventDefault()
      void handleOcrImageFileRef.current(file, 'text_import_paste')
    }
    document.addEventListener('paste', handlePaste)
    return () => document.removeEventListener('paste', handlePaste)
  }, [])

  // ── File processing ─────────────────────────────────────────────────────────

  async function processFile(file: File) {
    setFileError(null)
    setLoadedFileName(null)
    setFileLoading(true)

    try {
      const name = file.name.toLowerCase()
      const ext = '.' + (name.split('.').pop() ?? '')

      if (file.size > MAX_FILE_BYTES) {
        setFileError('too_large')
        return
      }

      if (name.endsWith('.txt')) {
        const content = await file.text()
        setText(content)
        setLoadedFileName(file.name)
        trackEvent('file_upload_txt')
      } else if (name.endsWith('.csv')) {
        // Parsing, mapping and the target-deck choice all happen on
        // CSVImportModal — this page only hands off the file.
        trackEvent('file_upload_csv')
        setCsvFile(file)
      } else if (SERVER_EXTRACTED_EXTS.has(ext)) {
        // Server-side text extraction for PDF / PPTX / DOCX
        setExtractError(null)
        // A 0-byte file can only fail, so it is not sent. The server rejects it
        // too (EMPTY_FILE); this only saves the round trip.
        if (file.size === 0) {
          setFileError('empty')
          return
        }
        const form = new FormData()
        form.append('file', file)
        if (title.trim()) form.append('title', title.trim())
        trackEvent(`file_upload_${ext.slice(1)}`)
        uploadFile.mutate(form, {
          onSuccess: (data) => {
            // Pass through only what the person typed. A blank title is not
            // guessed here — create-deck resolves the fallback (source title,
            // then a cleaned filename, then the text itself) in one place on
            // the server, so this page never holds a second, diverging copy
            // of that decision.
            navigate(`/text-import/${data.text_import_id}/review`, {
              state: { extractResponse: data, title: title.trim() || null },
            })
          },
          onError: (err: unknown) => {
            setExtractError(fileUploadErrorMessage(err, file.name, ti))
          },
        })
      } else {
        setFileError('unsupported')
      }
    } catch {
      setFileError('read_failed')
    } finally {
      setFileLoading(false)
    }
  }

  function handleFileInput(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (file) processFile(file)
    // Reset so the same file can be selected again
    e.target.value = ''
  }

  // ── Drag-and-drop ───────────────────────────────────────────────────────────

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(true)
  }

  function handleDragLeave(e: React.DragEvent) {
    // Only reset when leaving the drop zone itself, not a child element
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) processFile(file)
  }

  // ── Text extraction submit ──────────────────────────────────────────────────

  const urlMode = isUrl(text)

  // Resolve the effective target deck: pre-targeted takes priority; picker selection second.
  const effectiveTargetDeckId = importMode === 'existing' ? (pickerDeckId ?? null) : null
  const selectedPickerDeck = ownedDecks.find((d) => d.id === pickerDeckId) ?? null

  // The review page reads title/target-deck fields from location.state (they're
  // small, unlike the draft — see textImportFlowState.ts for why the draft and
  // the extraction itself are never put in history state).
  function navigateToReview(textImportId: string, extractResponse: TextImportExtractResponse) {
    navigate(`/text-import/${textImportId}/review`, {
      state: {
        extractResponse,
        title: title.trim() || null,
        targetDeckId: effectiveTargetDeckId,
        targetDeckTitle: effectiveTargetDeckId
          ? (preTargetedDeckTitle ?? selectedPickerDeck?.title ?? null)
          : null,
      },
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!text.trim()) return

    setExtractError(null)

    // Going back to step 1 then forward again without touching the text
    // reuses the extraction already paid for, instead of re-hitting the
    // endpoint and minting another orphan TextImport row.
    if (cachedExtraction && cachedExtraction.text === text.trim()) {
      navigateToReview(cachedExtraction.extractResponse.text_import_id, cachedExtraction.extractResponse)
      return
    }

    const payload = urlMode
      ? { url: text.trim(), title: title.trim() || null }
      : { text: text.trim(), title: title.trim() || null }
    const textImportMode = effectiveTargetDeckId ? 'existing_deck' : 'new_deck'
    trackEvent(urlMode ? 'url_import_started' : 'text_import_started', { text_import_mode: textImportMode })
    extract.mutate(
      payload,
      {
        onSuccess: (data) => {
          setCachedExtraction({ text: text.trim(), extractResponse: data })
          navigateToReview(data.text_import_id, data)
        },
        onError: (err: unknown) => {
          setExtractError(textExtractErrorMessage(err, ti))
        },
      },
    )
  }

  // Explicit abandon — distinct from the review page's step-back control.
  // location.key is the router's own "default" sentinel for an entry that
  // was never reached by an in-app navigate() (a cold start or a deep
  // link straight to /text-import): navigate(-1) there would walk out of
  // the app entirely, so land on Home instead of underflowing history.
  function handleExit() {
    clearTextImportFlow()
    if (location.key === 'default') {
      navigate('/home', { replace: true })
    } else {
      navigate(-1)
    }
  }

  const fileErrorMessage: string | null = fileError === null ? null : {
    empty: ti.fileEmpty,
    too_large: ti.uploadErrTooLarge,
    unsupported: ti.uploadErrUnsupported,
    read_failed: ti.fileReadFailed,
  }[fileError]

  const ocrErrorMessage: string | null = ocrError === null ? null
    : ocrError.kind === 'too_large' ? ti.ocrErrTooLarge(ocrError.sizeMb)
    : ocrError.kind === 'no_text' ? ti.ocrErrNoText
    : ocrError.kind === 'decode' ? ti.ocrErrDecode
    : ti.ocrErrGeneric

  const charCount = text.length
  const isOverLimit = !urlMode && charCount > MAX_CHARS
  const isEmpty = text.trim().length === 0
  const needsDeckSelection = importMode === 'existing' && !effectiveTargetDeckId
  const isDisabled = isEmpty || isOverLimit || needsDeckSelection || extract.isPending || uploadFile.isPending || fileLoading

  // Human-readable reason for the disabled CTA
  const disabledReason: string | null = extract.isPending || uploadFile.isPending || fileLoading
    ? null
    : isEmpty
    ? ti.ctaReasonEmpty
    : isOverLimit
    ? ti.ctaReasonOverLimit
    : needsDeckSelection
    ? ti.ctaReasonNoDeck
    : null

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Page-scoped styles — removed when TextImportPage unmounts */}
      <style>{`
        @media (max-width: 767px) {
          .ti-cta-sticky {
            position: fixed;
            bottom: calc(60px + env(safe-area-inset-bottom, 0px));
            left: 0;
            right: 0;
            z-index: 20;
          }
        }
        @media (min-width: 768px) {
          .ti-cta-sticky {
            position: sticky;
            bottom: 0;
          }
        }
      `}</style>

      {/* Hidden inputs — always mounted so refs stay valid */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.csv,.pdf,.pptx,.docx,text/plain,text/csv,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        style={{ display: 'none' }}
        onChange={handleFileInput}
      />
      <input
        ref={ocrImageInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        aria-hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleOcrImageFile(file, 'text_import_gallery')
          e.target.value = ''
        }}
      />
      {/* capture="environment" routes directly to camera on Android, bypasses chooser */}
      <input
        ref={ocrCameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        aria-hidden
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleOcrImageFile(file, 'text_import_camera')
          e.target.value = ''
        }}
      />

      {/* CSV files open the shared import surface — this page never builds a
          CSV import request itself. No target is pre-selected. */}
      <CSVImportModal
        isOpen={!!csvFile}
        initialFile={csvFile ?? undefined}
        onClose={() => setCsvFile(null)}
      />

      <form
          onSubmit={handleSubmit}
          className="flex flex-col"
          style={{ color: 'var(--text-primary)' }}
        >
          {/* Scrollable content — max-md:pb-28 clears the fixed CTA zone on mobile */}
          <div className="flex-1 max-w-xl mx-auto w-full flex flex-col gap-4 py-4 sm:py-8 max-md:pb-28">

            {/* Title row + exit control — the only way out of this flow apart
                from the browser/hardware back gesture (which already works,
                since this step is a real route). Distinct from WordReview's
                "← Back" (a step back): this leaves the flow entirely. */}
            <div className="flex items-start justify-between gap-3">
              <h1
                className="font-bold"
                style={{ color: 'var(--text-primary)', fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 }}
              >
                {ti.heroTitle}
              </h1>
              <button
                type="button"
                onClick={handleExit}
                aria-label={ti.closeAria}
                className="flex items-center justify-center rounded-full transition-opacity hover:opacity-70 shrink-0"
                style={{ width: 32, height: 32, background: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: 'none', cursor: 'pointer' }}
              >
                <X size={16} />
              </button>
            </div>

            {/* Deck target */}
            {preTargetActive ? (
              /* Pre-targeted chip: came from DeckDetailPage */
              <div
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm"
                style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent)' }}
              >
                <span style={{ fontSize: 16 }}>📥</span>
                <span style={{ color: 'var(--text-secondary)', flex: 1 }}>
                  {ti.addingTo} <strong style={{ color: 'var(--text-primary)' }}>{preTargetedDeckTitle ?? ti.selectedDeckFallback}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setPreTargetActive(false)
                    setImportMode('new')
                    setPickerDeckId(null)
                  }}
                  aria-label={ti.createNewDeckInstead}
                  className="text-xs font-medium hover:opacity-70 transition-opacity"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)' }}
                >
                  {ti.createNewDeckInstead}
                </button>
              </div>
            ) : (
              /* Pill segmented control */
              <div
                className="flex p-1 rounded-full"
                style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
              >
                <button
                  type="button"
                  onClick={() => { setImportMode('new'); setPickerOpen(false) }}
                  className="flex-1 rounded-full text-sm font-semibold transition-all duration-150 flex items-center justify-center"
                  style={{
                    height: 40,
                    background: importMode === 'new' ? 'var(--accent-fill)' : 'transparent',
                    color: importMode === 'new' ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                    boxShadow: importMode === 'new' ? '0 2px 8px rgba(74,109,140,0.25)' : 'none',
                  }}
                >
                  {ti.createNewDeck}
                </button>
                <button
                  type="button"
                  onClick={() => { setImportMode('existing'); setPickerOpen(true) }}
                  className="flex-1 rounded-full text-sm font-semibold transition-all duration-150 flex items-center justify-center"
                  style={{
                    height: 40,
                    background: importMode === 'existing' ? 'var(--accent-fill)' : 'transparent',
                    color: importMode === 'existing' ? 'var(--text-on-accent)' : 'var(--text-secondary)',
                    boxShadow: importMode === 'existing' ? '0 2px 8px rgba(74,109,140,0.25)' : 'none',
                  }}
                >
                  {ti.addToExisting}
                </button>
              </div>
            )}

            {/* Deck picker — only in existing mode */}
            {!preTargetActive && importMode === 'existing' && (
              <DeckPicker
                ownedDecks={ownedDecks}
                selectedDeckId={pickerDeckId}
                search={deckSearch}
                isOpen={pickerOpen}
                loading={decksQuery.isPending}
                onSearchChange={setDeckSearch}
                onSelect={(id) => { setPickerDeckId(id); setPickerOpen(false) }}
                onToggleOpen={() => setPickerOpen((v) => !v)}
              />
            )}

            {/* Deck title toggle — centered, only in new-deck mode */}
            {!effectiveTargetDeckId && (
              <div className="text-center">
                <DeckTitleField
                  value={title}
                  onChange={setTitle}
                  disabled={extract.isPending}
                />
              </div>
            )}

            {/* OCR source hint */}
            {textFromOcr && text && (
              <div
                className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm"
                style={{ background: 'var(--accent-soft)', border: '1px solid var(--accent)' }}
              >
                <Camera size={13} style={{ color: 'var(--accent)', flexShrink: 0 }} aria-hidden />
                <span style={{ color: 'var(--text-secondary)', flex: 1 }}>
                  {ti.ocrHint}
                </span>
                <button
                  type="button"
                  onClick={() => setTextFromOcr(false)}
                  aria-label={ti.dismissOcrHint}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '0 2px', display: 'flex', alignItems: 'center' }}
                >
                  <X size={13} />
                </button>
              </div>
            )}

            {/* Textarea — drop-target wrapper */}
            <div
              className="relative"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {/* Loaded-file chip */}
              {loadedFileName && (
                <div className="absolute top-3 right-3 z-10">
                  <span
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full"
                    style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}
                  >
                    <CheckCircle2 size={11} />
                    {loadedFileName}
                    <button
                      type="button"
                      onClick={() => { setText(''); setLoadedFileName(null); setTextFromOcr(false) }}
                      className="hover:opacity-70 transition-opacity"
                      style={{ color: 'var(--accent)', lineHeight: 1, marginLeft: 2 }}
                      aria-label={ti.removeFile}
                    >
                      <X size={11} />
                    </button>
                  </span>
                </div>
              )}

              {/* Clear button */}
              {text.length > 0 && !loadedFileName && !extract.isPending && !fileLoading && (
                <button
                  type="button"
                  onClick={() => { setText(''); setLoadedFileName(null); setTextFromOcr(false) }}
                  className="absolute top-3 right-3 z-10 flex items-center justify-center w-7 h-7 rounded-full transition-opacity hover:opacity-70"
                  style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}
                  aria-label={ti.clearText}
                >
                  <X size={13} />
                </button>
              )}

              <textarea
                ref={textareaRef}
                id="import-text"
                aria-label={ti.yourText}
                value={text}
                onChange={(e) => { setText(e.target.value); setExtractError(null) }}
                placeholder={ti.textareaPlaceholder}
                rows={urlMode ? 2 : 4}
                disabled={extract.isPending || fileLoading}
                className="w-full px-4 rounded-2xl text-base resize-none transition-colors overflow-hidden"
                style={{
                  background: isDragging ? 'var(--accent-soft)' : 'var(--bg-surface)',
                  border: `1px solid ${
                    isDragging ? 'var(--accent)'
                    : isOverLimit ? 'var(--danger)'
                    : urlMode ? 'var(--accent)'
                    : 'var(--border)'
                  }`,
                  color: 'var(--text-primary)',
                  outline: 'none',
                  fontFamily: 'var(--font-content, var(--font-ui))',
                  lineHeight: '1.5',
                  minHeight: 112,
                  paddingTop: loadedFileName ? '3rem' : '14px',
                  paddingBottom: '14px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  transition: 'border-color 0.15s, background 0.15s',
                }}
                onFocus={(e) => { if (!isOverLimit) e.currentTarget.style.borderColor = 'var(--accent)' }}
                onBlur={(e) => { e.currentTarget.style.borderColor = isOverLimit ? 'var(--danger)' : urlMode ? 'var(--accent)' : 'var(--border)' }}
              />

              {/* URL-mode indicator */}
              {urlMode && (
                <div
                  className="absolute bottom-3 left-4 flex items-center gap-1.5 text-xs pointer-events-none"
                  style={{ color: 'var(--accent)' }}
                >
                  <Link size={11} />
                  {ti.articleFetched}
                </div>
              )}

              {/* Character counter */}
              <div
                className="absolute bottom-3 right-4 text-xs tabular-nums pointer-events-none"
                style={{
                  color: isOverLimit
                    ? 'var(--danger)'
                    : charCount > MAX_CHARS * 0.9
                    ? 'var(--warning-text)'
                    : 'var(--text-secondary)',
                  opacity: charCount === 0 ? 0 : 1,
                  transition: 'opacity 0.15s',
                }}
              >
                {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}
              </div>
            </div>

            {/* Over-limit message */}
            {isOverLimit && (
              <p className="text-xs -mt-2" style={{ color: 'var(--danger)' }}>
                {ti.overLimit(charCount - MAX_CHARS)}
              </p>
            )}

            {/* OCR progress/error */}
            {ocrPhase !== 'idle' && (
              <div>
                {(ocrPhase === 'downloading' || ocrPhase === 'recognizing') && (
                  <div
                    className="flex flex-col gap-2 px-4 py-3 rounded-xl"
                    style={{ background: 'var(--bg-surface)', border: '1.5px solid var(--border)' }}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                        {ocrPhase === 'downloading' ? ti.ocrDownloading : ti.ocrReading}
                      </span>
                      <button
                        type="button"
                        onClick={cancelOcr}
                        className="text-xs px-2.5 py-1 rounded-lg transition-opacity hover:opacity-70"
                        style={{ border: '1px solid var(--border)', color: 'var(--text-secondary)', background: 'none' }}
                      >
                        {ti.cancel}
                      </button>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: 'var(--border)', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: `${Math.round(ocrProgressFraction * 100)}%`,
                        background: 'var(--accent)',
                        borderRadius: 2,
                        transition: 'width 0.2s ease',
                      }} />
                    </div>
                    <div className="flex justify-end items-center">
                      <span className="text-xs tabular-nums" style={{ color: 'var(--text-secondary)' }}>
                        {Math.round(ocrProgressFraction * 100)}%
                      </span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.65 }}>
                      {ti.ocrBeta}
                    </p>
                  </div>
                )}

                {ocrPhase === 'error' && (
                  <div
                    role="alert"
                    className="flex items-start gap-2.5 px-4 py-3 rounded-xl text-sm"
                    style={{
                      background: 'var(--danger-soft)',
                      border: '1px solid var(--danger)',
                      color: 'var(--text-primary)',
                    }}
                  >
                    <AlertCircle size={15} className="mt-0.5 shrink-0" style={{ color: 'var(--danger)' }} />
                    <span style={{ flex: 1 }}>{ocrErrorMessage}</span>
                    <button
                      type="button"
                      onClick={() => { cancelOcr(); setTimeout(() => ocrImageInputRef.current?.click(), 50) }}
                      className="text-xs font-semibold underline underline-offset-2 hover:opacity-70 shrink-0"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}
                    >
                      {/* Every kind but an unexplained failure would fail again on
                          the same image; the button opens the picker either way. */}
                      {ocrError?.kind === 'generic' ? ti.ocrRetry : ti.chooseOtherImage}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* File processing states */}
            {(fileLoading || uploadFile.isPending) && (
              <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm"
                   style={{ background: 'var(--bg-surface)', border: '1.5px solid var(--border)', color: 'var(--text-secondary)' }}>
                <div className="w-4 h-4 rounded-full border-2 animate-spin shrink-0"
                     style={{ borderColor: 'var(--accent)', borderTopColor: 'transparent' }} />
                {uploadFile.isPending ? ti.extractingFile : ti.readingFile}
              </div>
            )}

            {fileError && (
              <div
                role="alert"
                className="flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm"
                style={{ background: 'var(--danger-soft)', border: '1px solid var(--danger)', color: 'var(--text-primary)' }}
              >
                <AlertCircle size={15} className="shrink-0" style={{ color: 'var(--danger)' }} />
                <span className="flex-1">{fileErrorMessage}</span>
                <button
                  type="button"
                  onClick={() => { setFileError(null); fileInputRef.current?.click() }}
                  className="text-xs font-semibold underline underline-offset-2 hover:opacity-70 shrink-0"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-primary)' }}
                >
                  {fileError === 'read_failed' ? ti.clickRetry : ti.chooseOtherFile}
                </button>
              </div>
            )}

            {/* OR IMPORT FROM */}
            {ocrPhase === 'idle' && !urlMode && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  <span
                    className="text-xs font-semibold uppercase"
                    style={{ color: 'var(--text-secondary)', letterSpacing: '0.05em' }}
                  >
                    {ti.orImportFrom}
                  </span>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <ImportTile
                    icon={<Camera size={22} style={{ color: 'var(--accent)' }} />}
                    label={ti.takePhoto}
                    sub={ti.tileScanPage}
                    beta
                    onClick={() => ocrCameraInputRef.current?.click()}
                    disabled={extract.isPending || fileLoading}
                  />
                  <ImportTile
                    icon={<Images size={22} style={{ color: 'var(--accent)' }} />}
                    label={ti.fromGallery}
                    sub={ti.tilePickImage}
                    onClick={() => ocrImageInputRef.current?.click()}
                    disabled={extract.isPending || fileLoading}
                  />
                  <ImportTile
                    icon={<File size={22} style={{ color: 'var(--accent)' }} />}
                    label={ti.uploadFile}
                    sub={ti.tileFileFormats}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={extract.isPending || fileLoading}
                  />
                </div>

                {/* Desktop paste hint */}
                <p
                  className="hidden md:block text-center text-xs"
                  style={{ color: 'var(--text-secondary)', opacity: 0.55 }}
                >
                  {ti.pasteImage(isMac ? '⌘V' : 'Ctrl+V')}
                </p>
              </div>
            )}

            {/* Extraction error */}
            {extractError && (
              <div
                role="alert"
                className="flex items-start gap-2.5 px-4 py-3 rounded-xl text-sm"
                style={{
                  background: 'var(--danger-soft)',
                  border: '1px solid var(--danger)',
                  // Message text on the red wash: --danger measures 3.2:1 there in
                  // light theme, under the 4.5:1 small-text floor. Red stays on
                  // the border, the wash and the icon.
                  color: 'var(--text-primary)',
                }}
              >
                <AlertCircle size={16} className="mt-0.5 shrink-0" style={{ color: 'var(--danger)' }} />
                <span>{extractError}</span>
              </div>
            )}
          </div>

          {/* ── Sticky CTA — fixed on mobile (position set via .ti-cta-sticky in <style>), sticky on desktop ── */}
          <div
            className="ti-cta-sticky"
            style={{
              background: 'var(--bg-page)',
              borderTop: '1px solid var(--border)',
              padding: '10px 16px 12px',
            }}
          >
            <div className="max-w-xl mx-auto w-full flex flex-col gap-2">
              <button
                type="submit"
                disabled={isDisabled}
                className="w-full flex items-center justify-center gap-2 h-12 rounded-full
                           font-semibold text-base transition-all duration-150 active:scale-[0.98]"
                style={{
                  background: isDisabled ? 'var(--bg-secondary)' : 'var(--accent-fill)',
                  color: isDisabled ? 'var(--text-secondary)' : 'var(--text-on-accent)',
                  border: isDisabled ? '1px dashed var(--border)' : 'none',
                  cursor: isDisabled && !extract.isPending && !uploadFile.isPending && !fileLoading
                    ? 'default'
                    : 'pointer',
                  boxShadow: isDisabled ? 'none' : '0 4px 16px rgba(74,109,140,0.35)',
                }}
              >
                {extract.isPending ? (
                  <>
                    <div
                      className="w-4 h-4 rounded-full border-2 animate-spin shrink-0"
                      style={{ borderColor: 'var(--text-on-dark)', borderTopColor: 'transparent' }}
                    />
                    {urlMode ? ti.fetchingArticle : ti.extractingWords}
                  </>
                ) : (
                  <>
                    {urlMode ? (
                      <><Link size={16} />{ti.fetchAndExtract}</>
                    ) : (
                      <>{ti.extractWords}<ArrowRight size={18} /></>
                    )}
                  </>
                )}
              </button>

              {/* Inline reason for disabled state */}
              {disabledReason && (
                <p className="text-xs text-center" style={{ color: 'var(--text-secondary)', opacity: 0.7 }}>
                  {disabledReason}
                </p>
              )}
            </div>
          </div>
        </form>
    </>
  )
}

// ── ImportTile ─────────────────────────────────────────────────────────────────

interface ImportTileProps {
  icon: React.ReactNode
  label: string
  sub: string
  beta?: boolean
  onClick: () => void
  disabled?: boolean
}

function ImportTile({ icon, label, sub, beta, onClick, disabled }: ImportTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center justify-center rounded-2xl transition-all hover:opacity-80
                 disabled:opacity-40 disabled:cursor-not-allowed active:scale-[0.97]"
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        minHeight: 76,
        padding: '10px 4px',
        gap: 6,
        position: 'relative',
      }}
    >
      {beta && (
        <span
          className="absolute font-bold uppercase"
          style={{
            top: 6, right: 6,
            fontSize: 9, letterSpacing: '0.05em',
            background: 'var(--accent-soft)', color: 'var(--accent)',
            borderRadius: 50, padding: '2px 7px',
            lineHeight: 1.4,
          }}
        >
          BETA
        </span>
      )}
      <div className="flex items-center justify-center">
        {icon}
      </div>
      <span
        className="leading-tight text-center font-medium"
        style={{ color: 'var(--text-primary)', fontSize: 13 }}
      >
        {label}
      </span>
      <span
        className="leading-tight text-center font-medium"
        style={{ color: 'var(--text-secondary)', fontSize: 10 }}
      >
        {sub}
      </span>
    </button>
  )
}

// ── DeckPicker ────────────────────────────────────────────────────────────────

interface DeckPickerProps {
  ownedDecks: { id: string; title: string }[]
  selectedDeckId: string | null
  search: string
  isOpen: boolean
  loading: boolean
  onSearchChange: (v: string) => void
  onSelect: (id: string) => void
  onToggleOpen: () => void
}

function DeckPicker({ ownedDecks, selectedDeckId, search, isOpen, loading, onSearchChange, onSelect, onToggleOpen }: DeckPickerProps) {
  const { tr } = useLocale()
  const ti = tr.textImport
  const selectedDeck = ownedDecks.find((d) => d.id === selectedDeckId)

  return (
    <div className="relative">
      <button
        type="button"
        onClick={onToggleOpen}
        className="w-full flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-sm transition-colors"
        style={{
          background: 'var(--bg-page)',
          border: '1.5px solid var(--border)',
          color: selectedDeck ? 'var(--text-primary)' : 'var(--text-secondary)',
          textAlign: 'left',
        }}
      >
        <span className="flex-1 truncate">
          {selectedDeck ? selectedDeck.title : ti.selectDeck}
        </span>
        <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.6, transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {isOpen && (
        <div
          className="absolute z-50 w-full mt-1 rounded-xl overflow-hidden flex flex-col"
          style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-float)', maxHeight: 260 }}
        >
          <div className="flex items-center gap-2 px-3 py-2.5" style={{ borderBottom: '1px solid var(--border)' }}>
            <Search size={13} style={{ color: 'var(--text-secondary)', flexShrink: 0 }} />
            <input
              type="text"
              placeholder={ti.searchDecksPlaceholder}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              autoFocus
              className="flex-1 text-sm outline-none"
              style={{ background: 'transparent', border: 'none', color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}
            />
          </div>
          <ul className="overflow-y-auto" style={{ maxHeight: 200 }}>
            {loading && (
              <li className="px-3.5 py-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>{ti.loadingDecks}</li>
            )}
            {!loading && ownedDecks.length === 0 && (
              <li className="px-3.5 py-2.5 text-sm" style={{ color: 'var(--text-secondary)' }}>{ti.noDecksFoundPicker}</li>
            )}
            {ownedDecks.map((deck) => (
              <li key={deck.id}>
                <button
                  type="button"
                  onClick={() => onSelect(deck.id)}
                  className="w-full text-left px-3.5 py-2.5 text-sm transition-colors hover:opacity-80"
                  style={{
                    background: deck.id === selectedDeckId ? 'var(--accent-soft)' : 'transparent',
                    color: deck.id === selectedDeckId ? 'var(--accent)' : 'var(--text-primary)',
                    fontWeight: deck.id === selectedDeckId ? 600 : 400,
                    display: 'block',
                  }}
                >
                  {deck.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

// ── DeckTitleField — collapsed by default, centered trigger ───────────────────

interface DeckTitleFieldProps {
  value: string
  onChange: (v: string) => void
  disabled: boolean
}

function DeckTitleField({ value, onChange, disabled }: DeckTitleFieldProps) {
  const { tr } = useLocale()
  const ti = tr.textImport
  const [open, setOpen] = useState(false)

  return (
    <div>
      {open ? (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <label
              htmlFor="import-title"
              className="text-sm font-medium"
              style={{ color: 'var(--text-secondary)' }}
            >
              {ti.deckTitleLabel}
            </label>
            <button
              type="button"
              onClick={() => { setOpen(false); onChange('') }}
              className="text-xs hover:opacity-70 transition-opacity"
              style={{ color: 'var(--text-secondary)' }}
            >
              {ti.deckAutoGenerate}
            </button>
          </div>
          <input
            id="import-title"
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            maxLength={200}
            placeholder={ti.deckTitlePlaceholder}
            disabled={disabled}
            autoFocus
            className="w-full px-4 py-2.5 rounded-xl text-sm transition-colors"
            style={{
              background: 'var(--bg-surface)',
              border: '1.5px solid var(--border)',
              color: 'var(--text-primary)',
              outline: 'none',
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = 'var(--accent)')}
            onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
          />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-sm transition-opacity hover:opacity-80"
          style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          {ti.deckAddTitle}{' '}
          <span style={{ color: 'var(--text-secondary)', fontWeight: 400 }}>{ti.deckAddTitleHint}</span>
        </button>
      )}
    </div>
  )
}

